// ─── La escala de notas del seminario: UNA sola ─────────────────────────────
// Hasta ahora había dos: `config.grading` (1-7 o 0-100, la del libro de notas)
// y `config.transcriptScale` ('letter-4', la del expediente importado). Un
// seminario con letras calificaba en el libro de notas de 1 a 7 y el expediente
// no podía leer esas notas. Ahora hay una escala por seminario, numérica o de
// letras, y TODA nota final se escribe y se lee con ella.
//
// COMPATIBILIDAD SIN MIGRAR: `resolveGradingScale` lee el campo nuevo y, si no
// está, los dos viejos. Un seminario sin configuración sigue en 1-7 como
// siempre; uno con `transcriptScale: 'letter-4'` (LABTS) se lee como el preset
// de letras. Nadie corre un script.
export const NUMERIC_SCALE_PRESETS = {
    '1-7': {
        kind: 'numeric',
        preset: '1-7',
        min: 1,
        max: 7,
        passing: 4,
        decimals: 1,
        exigencia: 0.6,
    },
    '0-10': { kind: 'numeric', preset: '0-10', min: 0, max: 10, passing: 6, decimals: 1 },
    '0-20': { kind: 'numeric', preset: '0-20', min: 0, max: 20, passing: 11, decimals: 0 },
    '0-100': { kind: 'numeric', preset: '0-100', min: 0, max: 100, passing: 60, decimals: 1 },
};
/** La de 4 puntos por unidad: A 4, B 3, C 2, D 1 (aprueban), F 0 (reprueba). */
export const LETTER_4_SCALE = {
    kind: 'letter',
    preset: 'letter-4',
    signsIgnored: true,
    rows: [
        { label: 'A', points: 4, minPercent: 90, passes: true },
        { label: 'B', points: 3, minPercent: 80, passes: true },
        { label: 'C', points: 2, minPercent: 70, passes: true },
        { label: 'D', points: 1, minPercent: 60, passes: true },
        { label: 'F', points: 0, minPercent: 0, passes: false },
    ],
};
/** La de siempre: 1 a 7, aprueba el 4 con el 60 % de logro. */
export const DEFAULT_GRADING_SCALE = NUMERIC_SCALE_PRESETS['1-7'];
/** LA escala del seminario. El campo nuevo gana; si no está, los viejos. */
export function resolveGradingScale(config) {
    if (config?.gradingScale)
        return config.gradingScale;
    if (config?.transcriptScale === 'letter-4')
        return LETTER_4_SCALE;
    const legacy = config?.grading;
    if (legacy?.scale === '0-100')
        return NUMERIC_SCALE_PRESETS['0-100'];
    if (legacy?.scale === '1-7') {
        const base = NUMERIC_SCALE_PRESETS['1-7'];
        // La exigencia declarada viaja; sin ella, la recta única (era la regla).
        const { exigencia: _default, ...rest } = base;
        return legacy.exigencia !== undefined ? { ...rest, exigencia: legacy.exigencia } : rest;
    }
    return DEFAULT_GRADING_SCALE;
}
/** Una escala bien formada; si no, por qué no (código de error). */
export function gradingScaleProblem(scale) {
    if (scale.kind === 'numeric') {
        const p = NUMERIC_SCALE_PRESETS[scale.preset];
        if (!p || scale.min !== p.min || scale.max !== p.max)
            return 'grading.scale_invalid';
        if (!(scale.passing > scale.min && scale.passing <= scale.max))
            return 'grading.passing_out_of_range';
        if (!Number.isInteger(scale.decimals) || scale.decimals < 0 || scale.decimals > 2) {
            return 'grading.scale_invalid';
        }
        if (scale.exigencia !== undefined && !(scale.exigencia > 0 && scale.exigencia < 1)) {
            return 'grading.scale_invalid';
        }
        return null;
    }
    const rows = scale.rows;
    if (rows.length < 2 || rows.length > 30)
        return 'grading.letters_invalid';
    const labels = new Set();
    for (const r of rows) {
        const label = r.label.trim().toUpperCase();
        if (!label || label.length > 4 || labels.has(label))
            return 'grading.letters_invalid';
        labels.add(label);
        if (!(r.points >= 0 && r.points <= 100))
            return 'grading.letters_invalid';
        if (!(r.minPercent >= 0 && r.minPercent <= 100))
            return 'grading.letters_invalid';
    }
    // De mayor a menor, y alguna letra que cubra desde el 0 %.
    for (let i = 1; i < rows.length; i++) {
        if (rows[i].minPercent >= rows[i - 1].minPercent)
            return 'grading.letters_invalid';
    }
    if (rows[rows.length - 1].minPercent !== 0)
        return 'grading.letters_invalid';
    if (!rows.some((r) => r.passes) || !rows.some((r) => !r.passes)) {
        return 'grading.letters_invalid';
    }
    return null;
}
const roundTo = (n, decimals) => {
    const f = 10 ** decimals;
    return Math.round(n * f) / f;
};
/** La etiqueta de una nota numérica: el número con sus decimales, con punto. */
export function numericLabel(value, scale) {
    return roundTo(value, scale.decimals).toFixed(scale.decimals);
}
/** La nota de aprobación efectiva: la del curso (plantilla) o la de la escala. */
export function passingGradeOf(scale, coursePassing) {
    // UN CURSO PUEDE EXIGIR MÁS, NUNCA MENOS que la escala. Una plantilla con el
    // 4 de siempre en un seminario de 0 a 10 (o de 0 a 100) aprobaría con 4: y
    // desde que el servidor decide el estado con esta nota, eso quedaría escrito
    // como «aprobado» en un acta real.
    return typeof coursePassing === 'number' &&
        coursePassing >= scale.passing &&
        coursePassing <= scale.max
        ? coursePassing
        : scale.passing;
}
/** Convierte un logro (0..100) a la escala.
 *
 *  Numérica con exigencia: las DOS RECTAS, que se cruzan exactamente en la nota
 *  de aprobación (en 1-7 con 60 %: 60 % = 4,0). Sin exigencia, una recta de
 *  `min` a `max`. Letras: la letra cuyo mínimo alcanza el logro. */
export function percentToScaled(percent, scale, coursePassing) {
    const pct = Math.min(100, Math.max(0, percent));
    if (scale.kind === 'letter') {
        const row = scale.rows.find((r) => pct >= r.minPercent) ?? scale.rows[scale.rows.length - 1];
        return { value: row.points, label: row.label, passes: row.passes };
    }
    const p = pct / 100;
    // LAS RECTAS CRUZAN EN LA APROBACIÓN DE LA ESCALA, no en la del curso: el
    // 60 % es 4,0 en 1-7 para todos los cursos. Un curso que exige 5 no mueve la
    // conversión —el 5,0 sigue estando cerca del 73 %—: solo decide si aprueba.
    const pivot = scale.passing;
    const e = scale.exigencia;
    const raw = e === undefined || e <= 0 || e >= 1
        ? scale.min + (scale.max - scale.min) * p
        : p < e
            ? scale.min + (pivot - scale.min) * (p / e)
            : pivot + (scale.max - pivot) * ((p - e) / (1 - e));
    const value = roundTo(raw, scale.decimals);
    return {
        value,
        label: numericLabel(value, scale),
        passes: value >= passingGradeOf(scale, coursePassing),
    };
}
/** La fila de una letra escrita (con o sin signo), o null si no es de la tabla. */
export function letterRow(label, scale) {
    const l = label.trim().toUpperCase();
    const exact = scale.rows.find((r) => r.label.toUpperCase() === l);
    if (exact)
        return exact;
    if (!scale.signsIgnored)
        return null;
    const base = l.replace(/[+-]$/, '');
    return scale.rows.find((r) => r.label.toUpperCase() === base) ?? null;
}
/** Lee una nota final escrita en la escala: numérica (número en rango) o letra
 *  de la tabla. Null si no es una nota de esa escala (W, INC, vacío…). */
export function readScaledLabel(label, scale, coursePassing) {
    const raw = label.trim();
    if (!raw)
        return null;
    if (scale.kind === 'letter') {
        const row = letterRow(raw, scale);
        return row ? { value: row.points, label: raw.toUpperCase(), passes: row.passes } : null;
    }
    const n = Number(raw.replace(',', '.'));
    if (!Number.isFinite(n) || n < scale.min || n > scale.max)
        return null;
    return {
        value: n,
        label: numericLabel(n, scale),
        passes: n >= passingGradeOf(scale, coursePassing),
    };
}
/** Una nota numérica ingresada: dentro del rango y con sus decimales. */
export function scaledFromValue(value, scale, coursePassing) {
    if (!Number.isFinite(value) || value < scale.min || value > scale.max)
        return null;
    const v = roundTo(value, scale.decimals);
    return {
        value: v,
        label: numericLabel(v, scale),
        passes: v >= passingGradeOf(scale, coursePassing),
    };
}
//# sourceMappingURL=grading-scale.js.map